/**
 * formatApiError(err)
 *
 * Safely coerce an axios error (or any thrown value) to a human-readable
 * string. Solves React error #31 — FastAPI validation errors return
 * `detail` as an array of {type, loc, msg, input, ctx} objects, and
 * rendering that array as JSX children crashes the render tree.
 *
 * Usage:
 *   } catch (err) {
 *       toast.error(formatApiError(err) || "Something went wrong");
 *       setLoadError(formatApiError(err));
 *   }
 */

export const formatApiError = (err, fallback = "") => {
    if (!err) return fallback;
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        // FastAPI/pydantic v2: detail = [{type, loc, msg, input, ctx}, ...]
        return detail
            .map((e) => {
                if (typeof e === "string") return e;
                if (e && typeof e.msg === "string") {
                    const where = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
                    return where ? `${where}: ${e.msg}` : e.msg;
                }
                try { return JSON.stringify(e); } catch { return "validation error"; }
            })
            .join("; ");
    }
    if (detail && typeof detail === "object") {
        try { return JSON.stringify(detail); } catch { return fallback; }
    }
    if (typeof err === "string") return err;
    if (err?.message) return err.message;
    return fallback;
};
